'use client';

import { useMemo, type ReactNode } from 'react';
import { createListCollection } from '@ark-ui/react';
import { Box, HStack } from '@terra/ui-styled-system';
import { Button, MetaDefault } from '@terra/ui-design-system';
import { Cancel01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { capitalize } from 'radash';
import { MultiSelectFilter } from '../filters/MultiSelectFilter';
import {
  hasActiveAuditLogFlowFilters,
  type AuditLogFlowFilterDimension,
  type AuditLogFlowFilterOptions,
  type AuditLogFlowFilters,
} from '../../utils/group-expected-response-observations';

type AuditLogFiltersProps = {
  options: AuditLogFlowFilterOptions;
  filters: AuditLogFlowFilters;
  onFilterChange: ({
    dimension,
    values,
  }: {
    dimension: AuditLogFlowFilterDimension;
    values: string[];
  }) => void;
  onClearAll: () => void;
};
// Keep the raw verb as the filter value (that's what the query filters on),
// but map NONE/CREATE/UPDATE/DELETE to title-case, user-facing labels
// (NONE → Read) so the Action filter matches the row badges.
const ACTION_LABEL_KEY_BY_VERB: Record<string, string> = {
  NONE: 'read',
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
};

function buildCollection(
  values: string[],
  getLabel?: (value: string) => string,
) {
  return createListCollection({
    items: values.map((value) => ({
      label: getLabel ? getLabel(value) : value,
      value,
    })),
  });
}

function FilterSlot({
  width,
  children,
}: {
  width: string;
  children: ReactNode;
}) {
  return (
    <Box flexShrink={0} w={width} maxW={width}>
      {children}
    </Box>
  );
}

export function AuditLogFilters({
  options,
  filters,
  onFilterChange,
  onClearAll,
}: AuditLogFiltersProps) {
  const t = useTranslations('expectedResponseObservations');
  const endpointCollection = useMemo(() => {
    const labelById = new Map(
      options.endpoints.map((endpoint) => [endpoint.id, endpoint.label]),
    );

    return buildCollection(
      options.endpoints.map((endpoint) => endpoint.id),
      (id) => labelById.get(id) ?? id,
    );
  }, [options.endpoints]);
  const verbCollection = useMemo(
    () =>
      buildCollection(options.verbs, (verb) => {
        const key = ACTION_LABEL_KEY_BY_VERB[verb];

        return key ? t(`actionLabel.${key}`) : capitalize(verb);
      }),
    [options.verbs, t],
  );
  const entityCollection = useMemo(
    () => buildCollection(options.entities),
    [options.entities],
  );
  const descriptionCollection = useMemo(
    () => buildCollection(options.descriptions),
    [options.descriptions],
  );
  const hasFilterOptions =
    options.endpoints.length > 0 ||
    options.verbs.length > 0 ||
    options.entities.length > 0 ||
    options.descriptions.length > 0;

  if (!hasFilterOptions) {
    return null;
  }

  const hasActiveFilters = hasActiveAuditLogFlowFilters(filters);

  return (
    <Box
      p="1"
      borderBottomWidth="1px"
      borderColor="border.default"
      bg="bg.subtle"
      borderRadius="sm"
      mb={3}
    >
      <HStack
        gap="1"
        alignItems="center"
        flexWrap="nowrap"
        overflowX="auto"
        w="full"
      >
        {options.endpoints.length > 0 ? (
          <FilterSlot width="140px">
            <MultiSelectFilter
              collection={endpointCollection}
              values={filters.endpoints}
              onValueChange={(values) =>
                onFilterChange({ dimension: 'endpoints', values })
              }
              placeholder={t('filters.endpoint')}
              searchable
              minW="140px"
              maxHeight="240px"
            />
          </FilterSlot>
        ) : null}
        {options.verbs.length > 0 ? (
          <FilterSlot width="120px">
            <MultiSelectFilter
              collection={verbCollection}
              values={filters.verbs}
              onValueChange={(values) =>
                onFilterChange({ dimension: 'verbs', values })
              }
              placeholder={t('filters.verb')}
              searchable
              minW="120px"
              maxHeight="240px"
            />
          </FilterSlot>
        ) : null}
        {options.entities.length > 0 ? (
          <FilterSlot width="120px">
            <MultiSelectFilter
              collection={entityCollection}
              values={filters.entities}
              onValueChange={(values) =>
                onFilterChange({ dimension: 'entities', values })
              }
              placeholder={t('filters.entity')}
              searchable
              minW="120px"
              maxHeight="240px"
            />
          </FilterSlot>
        ) : null}
        {options.descriptions.length > 0 ? (
          <FilterSlot width="160px">
            <MultiSelectFilter
              collection={descriptionCollection}
              values={filters.descriptions}
              onValueChange={(values) =>
                onFilterChange({ dimension: 'descriptions', values })
              }
              placeholder={t('filters.description')}
              searchable
              minW="160px"
              maxHeight="240px"
            />
          </FilterSlot>
        ) : null}
        {hasActiveFilters ? (
          <Button size="xs" variant="ghost" onClick={onClearAll} h="6" px="2">
            <HStack gap="1" alignItems="center">
              <Cancel01Icon size={10} />
              <MetaDefault>{t('filters.clearAll')}</MetaDefault>
            </HStack>
          </Button>
        ) : null}
      </HStack>
    </Box>
  );
}
